import React, { useState } from 'react';
import {
    AspectRatioIcon,
    Download01Icon,
    ImageCompositionIcon,
    MagicWand01Icon,
    PaintBrush01Icon,
} from 'hugeicons-react';
import FeatureCards, { FeatureCardItem } from './FeatureCards';
import '../../Styles/Pricing/ImageGenShowcase.css';

const EXAMPLES = [
    {
        prompt: 'Make an abstract cover image for my presentation on renewable energy.',
        src: 'https://cdn.cognora.ca/feature%20images/image-gen-sample-2.jpeg',
        alt: 'Swirling abstract sphere of deep blue and glowing orange strands',
        delay: 250,
    },
    {
        prompt: 'Create a mountain landscape with a waterfall at sunset and wildflowers in the foreground.',
        src: 'https://cdn.cognora.ca/feature%20images/image-gen-sample1.jpeg',
        alt: 'Snowy peaks at sunset with a waterfall running into a valley of pink wildflowers',
        delay: 700,
    },
];

const FEATURES: FeatureCardItem[] = [
    {
        icon: <MagicWand01Icon />,
        title: 'Just Describe It',
        text: 'Say what you want in plain words and Buddy fills in the detail.',
    },
    {
        icon: <PaintBrush01Icon />,
        title: 'Edit Any Image',
        text: 'Upload a photo or pick a result and ask for a new style, or things added or removed.',
    },
    {
        icon: <ImageCompositionIcon />,
        title: 'Combine Images',
        text: 'Blend several images into one composite.',
    },
    {
        icon: <AspectRatioIcon />,
        title: 'Any Shape',
        text: 'Square, widescreen, portrait or phone wallpaper sizes.',
    },
];

const ImageGenShowcase: React.FC = () => {
    const [loaded, setLoaded] = useState<Record<number, boolean>>({});

    return (
        <section id="image-generation-showcase" className="image-showcase" aria-labelledby="image-showcase-heading">
            <div className="showcase-header">
                <h2 id="image-showcase-heading">Create and Edit Images</h2>
                <p>Describe an image and Buddy makes it right in the chat. Upload a photo or pick a result to change it.</p>
            </div>

            <div className="image-demo">
                {EXAMPLES.map((ex, i) => {
                    const ready = !!loaded[i];
                    return (
                        <figure key={ex.src} className="image-demo-turn" style={{ '--reveal-delay': `${ex.delay}ms` } as React.CSSProperties}>
                            <p className="image-demo-bubble">{ex.prompt}</p>
                            <div className={`image-demo-result ${ready ? 'is-ready' : ''}`} aria-busy={!ready}>
                                <img
                                    src={ex.src}
                                    alt={ex.alt}
                                    onLoad={() => setLoaded(l => ({ ...l, [i]: true }))}
                                />
                                <a
                                    className="image-demo-download"
                                    href={ex.src}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Open full-size image"
                                    tabIndex={ready ? 0 : -1}
                                >
                                    <Download01Icon size={16} />
                                </a>
                            </div>
                        </figure>
                    );
                })}
            </div>

            <FeatureCards items={FEATURES} label="What image generation does" />
        </section>
    );
};

export default ImageGenShowcase;
